/** @format */

const STREET_TYPOLOGY =
	/^\s*(calle|av\.?|avda\.?|avenida|pasaje|psje\.?|pje\.?|camino|ruta|diagonal)\b\.?/i;

const FILLER_WORDS = /\b(de|del|la|los|las|el|al)\b/g;

const normalizeText = (value) =>
	`${value || ''}`
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[.,]/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();

export const hasTypology = (street) => STREET_TYPOLOGY.test(street || '');

const normalizeStreetName = (street) =>
	normalizeText(street)
		.replace(STREET_TYPOLOGY, '')
		.replace(/\s+\d+\s*$/, '')
		.replace(/\s+/g, ' ')
		.trim();

const isWordPrefix = (long, short) => long === short || long.startsWith(`${short} `);

export const streetTokens = (street) =>
	normalizeStreetName(street)
		.replace(FILLER_WORDS, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.split(' ')
		.filter(Boolean)
		.filter((token) => token.length >= 4 || /^\d/.test(token));

export const streetNameMatches = (routeLongName, enteredStreet) => {
	const route = normalizeStreetName(routeLongName);
	const entered = normalizeStreetName(enteredStreet);
	if (!route || !entered) return false;
	if (isWordPrefix(route, entered) || isWordPrefix(entered, route)) return true;

	const enteredToks = streetTokens(enteredStreet);
	const routeToks = streetTokens(routeLongName);
	if (!enteredToks.length || !routeToks.length) return false;

	// Every significant entered token must appear in the route (blocks Talca "2 Oriente"
	// for "47 1/2 Oriente", allows "capachitos" ↔ "los Capachitos").
	return enteredToks.every((token) =>
		routeToks.some((routeToken) => routeToken === token || routeToken.includes(token) || token.includes(routeToken)),
	);
};

export const communeMatches = (addressComponents, selectedCommune) => {
	const selected = normalizeText(selectedCommune);
	if (!selected) return false;

	const selectedCompact = selected.replace(/[\s-]/g, '');
	const selectedTokens = selected.split(/\s+/).filter((t) => t.length >= 4);

	return (addressComponents || [])
		.filter(
			(component) =>
				component.types.includes('locality') ||
				component.types.includes('administrative_area_level_3'),
		)
		.some((component) => {
			const returned = normalizeText(component.long_name);
			if (!returned) return false;
			const returnedCompact = returned.replace(/[\s-]/g, '');

			if (
				isWordPrefix(returned, selected) ||
				isWordPrefix(selected, returned) ||
				returnedCompact.includes(selectedCompact) ||
				selectedCompact.includes(returnedCompact)
			) {
				return true;
			}

			return selectedTokens.some(
				(token) => returned.includes(token) || returnedCompact.includes(token),
			);
		});
};

const hasPreciseStreetSignal = (result, noNumber) => {
	const components = result.address_components || [];
	const hasRoute = components.some((c) => c.types.includes('route'));
	const hasNumber = components.some((c) => c.types.includes('street_number'));
	const locType = result.geometry?.location_type;
	const precise =
		locType === 'ROOFTOP' ||
		locType === 'RANGE_INTERPOLATED' ||
		(result.types || []).includes('street_address');
	return hasRoute && precise && (noNumber || hasNumber);
};

// Prefer street+commune; if commune mismatches but Google found a precise street pin, still accept.
export const pickMatchingResult = (results, enteredStreet, selectedCommune, noNumber = false) => {
	if (!Array.isArray(results)) return null;

	const withStreet = results.filter((result) => {
		const route = (result.address_components || []).find((c) => c.types.includes('route'));
		return route && streetNameMatches(route.long_name, enteredStreet);
	});

	const withCommune = withStreet.find((result) =>
		communeMatches(result.address_components, selectedCommune),
	);
	if (withCommune) return withCommune;

	return withStreet.find((result) => hasPreciseStreetSignal(result, noNumber)) || null;
};

// Reject backend responses that are only a city/region (e.g. "Valparaíso, Chile").
export const isUsableBackendGeocode = (addressComplete, enteredStreet) => {
	const addr = normalizeText(addressComplete || '');
	if (!addr || !/\d/.test(addr)) return false;

	const tokens = streetTokens(enteredStreet).filter((t) => !/^\d/.test(t));
	if (!tokens.length) return true;
	return tokens.some((token) => addr.includes(token));
};

export const splitStreetNumber = (street) => {
	const match = `${street || ''}`.match(/^(.*?)[\s,]*(\d+)\s*$/);
	return match
		? { street: match[1].trim(), number: match[2] }
		: { street: street || '', number: '' };
};

export const cleanAddressPart = (value) =>
	`${value || ''}`.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
